import { NFT_PLUGINS_PKS } from '@constants/plugins';
import { BN } from '@coral-xyz/anchor';
import { BigNumber } from 'bignumber.js';
import { produce } from 'immer';

import {
  useRealmCommunityMintInfoQuery,
  useRealmCouncilMintInfoQuery,
} from '@hooks/queries/mintInfo';
import { useRealmConfigQuery } from '@hooks/queries/realmConfig';
import { ButtonToggle } from '@hub/components/controls/ButtonToggle';
import { Input } from '@hub/components/controls/Input';
import { Slider } from '@hub/components/controls/Slider';
import {
  CommunityRules,
  CouncilRules,
} from '@hub/components/EditWalletRules/types';
import { capitalize } from '@hub/lib/capitalize';
import { formatNumber } from '@hub/lib/formatNumber';
import { FormProps } from '@hub/types/FormProps';

import { GovernanceTokenType } from '@hub/types/GovernanceTokenType';

import { SliderValue } from './SliderValue';
import { ValueBlock } from './ValueBlock';
import { VoteTippingSelector } from './VoteTippingSelector';

type Pop = 'community' | 'council';
interface Props
  extends FormProps<{
    rules: NonNullable<CouncilRules> | CommunityRules;
  }> {
  govPop: Pop;
}

export function VetoQuorumPercent(props: Props) {
  const otherPop = props.govPop === 'community' ? 'council' : 'community';

  return (
    <ValueBlock
      title={`${capitalize(props.govPop)} Veto Voting Quorum`}
      description={
        <>
          The percentage of <span className="font-bold">No</span> votes required
          to veto a {otherPop} proposal
        </>
      }
    >
      <div className="grid grid-cols-[100px,1fr] gap-x-2 items-center">
        <SliderValue
          min={1}
          max={100}
          value={props.rules.vetoQuorumPercent}
          units="%"
          onChange={(value) => {
            const newRules = produce(props.rules, (data) => {
              data.vetoQuorumPercent = value;
            });
            props.onRulesChange?.(newRules);
          }}
        />
        <Slider
          min={1}
          max={100}
          trackColor="bg-sky-400"
          value={props.rules.vetoQuorumPercent}
          onChange={(value) => {
            const newRules = produce(props.rules, (data) => {
              data.vetoQuorumPercent = value;
            });
            props.onRulesChange?.(newRules);
          }}
          onRenderValue={(val) => `${val}%`}
        />
      </div>
    </ValueBlock>
  );
}

export function CanVeto(props: Props) {
  const otherPop = props.govPop === 'community' ? 'council' : 'community';

  return (
    <ValueBlock
      title={`Do you want your ${props.govPop} to have veto power over ${otherPop} proposals?`}
      description={`Your ${props.govPop} can veto a ${otherPop}-approved proposal.`}
    >
      <ButtonToggle
        className="h-14"
        value={props.rules.canVeto}
        onChange={(value) => {
          const newRules = produce(props.rules, (data) => {
            data.canVeto = value;
          });
          props.onRulesChange?.(newRules);
        }}
      />
    </ValueBlock>
  );
}

export function CanVote(props: Props) {
  return (
    <ValueBlock
      title={`Do you want to allow ${props.govPop} members to vote?`}
      description={`If disabled, the ${props.govPop} members can no longer vote on proposals.`}
    >
      <ButtonToggle
        className="h-14"
        value={props.rules.canVote}
        onChange={(value) => {
          const newRules = produce(props.rules, (data) => {
            data.canVote = value;
          });
          props.onRulesChange?.(newRules);
        }}
      />
    </ValueBlock>
  );
}

export function VotingPowerToCreateProposals(props: Props) {
  const communityTokenInfo = useRealmCommunityMintInfoQuery();
  const councilTokenInfo = useRealmCouncilMintInfoQuery();
  const config = useRealmConfigQuery().data?.result;

  const tokenInfoQuery =
    props.rules.tokenType === GovernanceTokenType.Community
      ? communityTokenInfo
      : councilTokenInfo;

  const supply = tokenInfoQuery.data?.result
    ? new BigNumber(
        tokenInfoQuery.data?.result.supply
          .div(new BN(Math.pow(10, tokenInfoQuery.data.result.decimals)))
          .toString(),
      )
    : undefined;

  const councilPowerPercent = supply
    ? props.rules.votingPowerToCreateProposals
        .dividedBy(supply)
        .multipliedBy(100)
    : undefined;

  const isNftPlugin =
    config?.account.communityTokenConfig.voterWeightAddin &&
    NFT_PLUGINS_PKS.includes(
      config?.account.communityTokenConfig.voterWeightAddin?.toBase58(),
    );

  const isNftGovernanceWithoutCouncil =
    isNftPlugin && props.govPop === 'community' && !councilTokenInfo.data;

  return (
    <ValueBlock
      title={`What is the minimum amount of ${props.govPop} governance power required to create a proposal?`}
      description={`A user must have this many ${props.govPop} governance power in order to create a proposal.`}
    >
      {isNftGovernanceWithoutCouncil ? (
        <div className="text-sm dark:text-neutral-500 relative top-[-10px]">
          The council is not added. Max tokens can be 5
        </div>
      ) : (
        ''
      )}
      <div className="relative">
        <Input
          className="w-full pr-24"
          placeholder="amount of governance power"
          value={formatNumber(
            props.rules.votingPowerToCreateProposals,
            undefined,
            {
              maximumFractionDigits: 0,
            },
          )}
          onChange={(e) => {
            const text = e.currentTarget.value.replaceAll(/[^\d.-]/g, '');
            let value = text ? new BigNumber(text) : new BigNumber(0);

            if (
              isNftGovernanceWithoutCouncil &&
              value.isGreaterThan(new BigNumber(5))
            ) {
              value = new BigNumber(5);
            }
            const newRules = produce(props.rules, (data) => {
              data.votingPowerToCreateProposals = value;
            });
            props.onRulesChange?.(newRules);
          }}
        />
        <div className="absolute top-1/2 right-4 text-neutral-500 -translate-y-1/2">
          Tokens
        </div>
      </div>
      {councilPowerPercent && supply?.isGreaterThan(0) && (
        <div className="flex items-center justify-end">
          <div className="mt-1 text-xs text-neutral-500">
            {councilPowerPercent.isGreaterThan(0)
              ? councilPowerPercent.isLessThan(0.01)
                ? '<0.01'
                : formatNumber(councilPowerPercent, undefined, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  })
              : 0}
            % of token supply
          </div>
        </div>
      )}
    </ValueBlock>
  );
}

export function CanCreateProposal(props: Props) {
  return (
    <ValueBlock
      title={`Do you want to allow ${props.govPop} members to create proposals?`}
      description={`If disabled, the ${props.govPop} members can no longer create proposals.`}
    >
      <ButtonToggle
        className="h-14"
        value={props.rules.canCreateProposal}
        onChange={(value) => {
          const newRules = produce(props.rules, (data) => {
            data.canCreateProposal = value;
          });
          props.onRulesChange?.(newRules);
        }}
      />
    </ValueBlock>
  );
}

export function QuorumPercent(props: Props) {
  return (
    <ValueBlock
      title={`${capitalize(props.govPop)} Approval Quorum`}
      description="The percentage of Yes votes required to pass a proposal"
    >
      <div className="grid grid-cols-[100px,1fr] gap-x-2 items-center">
        <SliderValue
          min={1}
          max={100}
          value={props.rules.quorumPercent}
          units="%"
          onChange={(value) => {
            const newRules = produce(props.rules, (data) => {
              data.quorumPercent = value;
            });
            props.onRulesChange?.(newRules);
          }}
          integer
        />
        <Slider
          min={1}
          max={100}
          trackColor="bg-sky-400"
          value={props.rules.quorumPercent}
          onChange={(value) => {
            const newRules = produce(props.rules, (data) => {
              data.quorumPercent = value;
            });
            props.onRulesChange?.(newRules);
          }}
          onRenderValue={(val) => `${val}%`}
          step={1}
        />
      </div>
    </ValueBlock>
  );
}

export function VoteTipping(props: Props) {
  return (
    <ValueBlock
      title={`${capitalize(props.govPop)} Vote Tipping`}
      description="Decide when voting should end"
    >
      <VoteTippingSelector
        className="w-full"
        value={props.rules.voteTipping}
        onChange={(value) => {
          const newRules = produce(props.rules, (data) => {
            data.voteTipping = value;
          });
          props.onRulesChange?.(newRules);
        }}
      />
    </ValueBlock>
  );
}
